// @ts-nocheck
import { useState, useRef } from "react"
import { Navigate } from "react-router-dom"
import { supabaseClient } from "@/lib/supabase/client"
import { useAuth } from "@/components/layout/AuthProvider"
import { useToast } from "@/components/ui/ToastProvider"
import { motion } from "framer-motion"

export default function SettingsPage() {
  const { supabaseUser, user, refreshUser } = useAuth()
  const { success, error: showErr } = useToast()
  const [tab, setTab] = useState("profile")
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    display_name: user?.display_name || "",
    bio: (user as any)?.bio || "",
    website: (user as any)?.website || ""
  })
  const [usernameForm, setUsernameForm] = useState({ username: user?.username || "", checking: false })
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatar_url || null)
  const [bannerPreview, setBannerPreview] = useState<string | null>((user as any)?.banner_url || null)
  const avatarRef = useRef<HTMLInputElement>(null)
  const bannerRef = useRef<HTMLInputElement>(null)

  if (!supabaseUser) return <Navigate to="/auth/login" replace />

  const uploadFile = async (file: File, bucket: string, path: string) => {
    const { error } = await supabaseClient.storage.from(bucket).upload(path, file, { upsert: true, contentType: file.type })
    if (error) throw error
    const { data: { publicUrl } } = supabaseClient.storage.from(bucket).getPublicUrl(path)
    return publicUrl
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { showErr("Too large", "Avatar must be under 5MB"); return }
    setUploadingAvatar(true)
    try {
      const url = await uploadFile(file, "avatars", `${supabaseUser.id}/avatar-${Date.now()}.${file.name.split(".").pop()}`)
      await supabaseClient.from("users").update({ avatar_url: url } as any).eq("id", supabaseUser.id)
      setAvatarPreview(url)
      await refreshUser()
      success("Avatar updated!")
    } catch (e: any) { showErr("Upload failed", e.message) }
    finally { setUploadingAvatar(false) }
  }

  const handleBannerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) { showErr("Too large", "Banner must be under 10MB"); return }
    setUploadingBanner(true)
    try {
      const url = await uploadFile(file, "banners", `${supabaseUser.id}/banner-${Date.now()}.${file.name.split(".").pop()}`)
      await supabaseClient.from("users").update({ banner_url: url } as any).eq("id", supabaseUser.id)
      setBannerPreview(url)
      await refreshUser()
      success("Banner updated!")
    } catch (e: any) { showErr("Upload failed", e.message) }
    finally { setUploadingBanner(false) }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true)
    try {
      const { error } = await supabaseClient.from("users").update({ display_name: form.display_name, bio: form.bio, website: form.website } as any).eq("id", supabaseUser.id)
      if (error) { showErr("Save failed", error.message); return }
      await refreshUser(); success("Profile saved!")
    } finally { setSaving(false) }
  }

  const handleUsernameChange = async (e: React.FormEvent) => {
    e.preventDefault()
    const newUsername = usernameForm.username.trim().toLowerCase()
    if (!newUsername || newUsername.length < 3) { showErr("Invalid", "Username must be at least 3 characters"); return }
    if (!/^[a-z0-9_]+$/.test(newUsername)) { showErr("Invalid", "Only lowercase letters, numbers and _ allowed"); return }
    if (newUsername === user?.username) { showErr("Same username", "Choose a different username"); return }

    setUsernameForm(f => ({ ...f, checking: true }))
    try {
      // Check uniqueness
      const { data: existing } = await supabaseClient.from("users").select("id").eq("username", newUsername).maybeSingle()
      if (existing) { showErr("Taken", "That username is already taken"); return }

      const { error } = await supabaseClient.from("users").update({ username: newUsername } as any).eq("id", supabaseUser.id)
      if (error) { showErr("Failed", error.message); return }
      await refreshUser()
      success("Username updated!")
    } catch (e: any) { showErr("Failed", e.message) }
    finally { setUsernameForm(f => ({ ...f, checking: false })) }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    const f = e.target as any
    const pw = f.password.value
    if (!pw || pw.length < 8) { showErr("Too short", "Password must be 8+ characters"); return }
    const { error } = await supabaseClient.auth.updateUser({ password: pw })
    if (error) showErr("Failed", error.message)
    else { success("Password updated!"); f.reset() }
  }

  const TABS = [{ k: "profile", l: "Profile" }, { k: "account", l: "Account" }, { k: "password", l: "Password" }]

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "1.5rem 1rem 6rem" }}>
      <h1 className="font-outfit font-bold text-2xl mb-6">Settings</h1>

      <div className="flex gap-1 mb-6 p-1 rounded-lg" style={{ background: "var(--bg3)" }}>
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className="flex-1 py-1.5 text-sm font-semibold rounded-md transition-all"
            style={{ background: tab === t.k ? "var(--bg2)" : "transparent", color: tab === t.k ? "var(--text)" : "var(--text2)" }}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="sq-panel p-5 mb-4">
            <h2 className="font-outfit font-bold text-base mb-4">Profile Photos</h2>

            <div className="mb-5">
              <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text3)" }}>Banner</p>
              <div className="relative w-full h-28 rounded-lg overflow-hidden mb-2 cursor-pointer group"
                style={{ background: "var(--bg3)", border: "1px solid var(--border)" }}
                onClick={() => bannerRef.current?.click()}>
                {bannerPreview ? <img src={bannerPreview} alt="Banner" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center">
                    <p className="text-sm" style={{ color: "var(--text3)" }}>Click to upload banner</p>
                  </div>
                }
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,.5)" }}>
                  <p className="text-sm font-bold text-white">{uploadingBanner ? "Uploading..." : "Change Banner"}</p>
                </div>
              </div>
              <input ref={bannerRef} type="file" accept="image/*" onChange={handleBannerChange} className="hidden" />
              <p className="text-xs" style={{ color: "var(--text3)" }}>Recommended: 1500×500px, max 10MB</p>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text3)" }}>Avatar</p>
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20 rounded-full overflow-hidden cursor-pointer group flex-shrink-0"
                  style={{ background: "var(--bg3)", border: "1px solid var(--border)" }}
                  onClick={() => avatarRef.current?.click()}>
                  {avatarPreview ? <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" /> :
                    <div className="w-full h-full flex items-center justify-center font-outfit font-black text-2xl" style={{ color: "var(--brand)" }}>{(user?.display_name || "?")[0].toUpperCase()}</div>
                  }
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,.5)" }}>
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </div>
                </div>
                <div>
                  <button onClick={() => avatarRef.current?.click()} className="sq-btn sq-btn-outline text-xs px-4 py-2 mb-1">{uploadingAvatar ? "Uploading..." : "Upload Avatar"}</button>
                  <p className="text-xs" style={{ color: "var(--text3)" }}>Max 5MB · JPG, PNG, GIF, WebP</p>
                </div>
              </div>
              <input ref={avatarRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
            </div>
          </div>

          <div className="sq-panel p-5">
            <h2 className="font-outfit font-bold text-base mb-4">Profile Info</h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider block mb-1" style={{ color: "var(--text3)" }}>Display Name</label>
                <input type="text" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} className="sq-input" maxLength={50} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider block mb-1" style={{ color: "var(--text3)" }}>Bio</label>
                <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} rows={3} className="sq-input resize-none" placeholder="Tell people about yourself" maxLength={300} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider block mb-1" style={{ color: "var(--text3)" }}>Website</label>
                <input type="url" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} className="sq-input" placeholder="https://" />
              </div>
              <button type="submit" disabled={saving} className="sq-btn sq-btn-primary px-6 py-2">{saving ? "Saving..." : "Save Changes"}</button>
            </form>
          </div>
        </motion.div>
      )}

      {tab === "account" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          {/* Username change */}
          <div className="sq-panel p-5 mb-4">
            <h2 className="font-outfit font-bold text-base mb-1">Change Username</h2>
            <p className="text-xs mb-4" style={{ color: "var(--text3)" }}>Usernames must be unique. Changing it will update your profile URL.</p>
            <form onSubmit={handleUsernameChange} className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider block mb-1" style={{ color: "var(--text3)" }}>Username</label>
                <div style={{ display: "flex", gap: ".5rem" }}>
                  <input
                    type="text"
                    value={usernameForm.username}
                    onChange={e => setUsernameForm(f => ({ ...f, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))}
                    className="sq-input"
                    maxLength={30}
                    placeholder="new_username"
                    style={{ flex: 1 }}
                  />
                  <button type="submit" disabled={usernameForm.checking || usernameForm.username === user?.username} className="sq-btn sq-btn-primary px-4 py-2 text-sm flex-shrink-0">
                    {usernameForm.checking ? "Checking..." : "Update"}
                  </button>
                </div>
                <p className="text-xs mt-1" style={{ color: "var(--text3)" }}>Current: u/{user?.username} · Letters, numbers and _ only</p>
              </div>
            </form>
          </div>

          <div className="sq-panel p-5">
            <h2 className="font-outfit font-bold text-base mb-3">Account Info</h2>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "var(--text3)" }}>Username</p>
                <p className="text-sm">u/{user?.username}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "var(--text3)" }}>Email</p>
                <p className="text-sm">{supabaseUser.email}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "var(--text3)" }}>Role</p>
                <p className="text-sm capitalize">{user?.role || "user"}</p>
              </div>
            </div>
          </div>
          <div className="sq-panel p-5 mt-4" style={{ borderColor: "rgba(239,68,68,.3)" }}>
            <h2 className="font-outfit font-bold text-base mb-2" style={{ color: "var(--danger)" }}>Danger Zone</h2>
            <p className="text-xs mb-3" style={{ color: "var(--text3)" }}>Once deleted, your account cannot be recovered.</p>
            <button onClick={async () => { if (confirm("Delete your account? This cannot be undone.")) await supabaseClient.auth.signOut() }}
              className="text-xs font-bold hover:underline" style={{ color: "var(--danger)" }}>Delete Account</button>
          </div>
        </motion.div>
      )}

      {tab === "password" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="sq-panel p-5">
            <h2 className="font-outfit font-bold text-base mb-4">Change Password</h2>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider block mb-1" style={{ color: "var(--text3)" }}>New Password</label>
                <input type="password" name="password" className="sq-input" placeholder="8+ characters" autoComplete="new-password" />
              </div>
              <button type="submit" className="sq-btn sq-btn-outline px-6 py-2">Update Password</button>
            </form>
          </div>
        </motion.div>
      )}
    </div>
  )
}
